<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
} from 'vue';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import {
  useActivities,
  useStateSync,
  useStringFunctions,
  useToastNotifications,
  useUtilities,
} from '@apparatix/composables';
import { useSelectionsStore } from '@apparatix/stores/useSelectionsStore';
import {
  savableActivityFilter,
  type Activity,
  type ActivityFilter,
  type ActivityFilterSection,
  type SavedActivityFilter,
} from '@apparatix/schemas/Activities';
import { Stack, SplitBar, Toolbar, Typography } from './core';
import { Noir } from '@apparatix/theme/Noir';
import { ObjectIconography } from '@apparatix/leads/SectionIconography';
import PageLayout from '@apparatix/leads/PageLayout.vue';
import ActivitiesHome from '@apparatix/leads/activities/HomePage.vue';
import ActivitiesPageSettings from '@apparatix/leads/activities/HomePageSettings.vue';
import AddActivityButton from '@apparatix/leads/activities/AddActivityButton.vue';
import { ActivityAddedEvent, ActivityDeletedEvent, ActivityUpdatedEvent } from '@apparatix/leads/activities/commonProps';
import { InjectEventNames } from '@apparatix/constants';
import ActivityFilterEditor from '@apparatix/leads/activities/ActivityFilterEditor.vue';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import { AppStore } from '@apparatix/stores/AppStore';

const {
  emptyActivityFilter,
  getFilteredActivities,
  getSavedFilters,
  resetActivityFiltersToDefault,
  saveFilters,
  transformVariableFilterDaysToFixedDates,
} = useActivities();

const { arrayHasValues, combineArrays, deepClone } = useUtilities();
const { showSuccess } = useToastNotifications();
const { getNumberAtEndOfString, isEmptyString } = useStringFunctions();
const { subscribe } = useStateSync(AppStore.tenantId, AppStore.currentUser.id);

subscribe<{ activity: Activity }>('ActivityUpdated', data => {
  activitySections.value.forEach(section => {
    if (!arrayHasValues(section.activities)) {
      return;
    }

    const idx = section.activities?.findIndex(activity => activity.id === data.activity.id);
    if (idx !== -1) {
      section.activities[idx] = { ...section.activities[idx], ...data.activity };
    }
  });
});

const selections = useSelectionsStore();

const isPageSettingsOpen = ref(false);
const isContentLoading = ref(true);
const isSaveFilterDialogOpen = ref(false);
const isMobileFilterOpen = ref(false);
const savedFilters = ref<SavedActivityFilter[]>([]);

const activitySearchResultsKey = ref(crypto.randomUUID());
const recentlyCreatedActivitiesKey = ref(crypto.randomUUID());

const recentlyCreated = ref<ActivityFilterSection>({
  activities: [],
  filterName: 'Recently created',
  key: recentlyCreatedActivitiesKey.value,
  isLoading: false,
  collapsedByDefault: false,
  includeCompleted: true,
  badgeSeverity: 'success',
  isVisible: false,
});

const searchResults = ref<ActivityFilterSection>({
  activities: [],
  filterName: 'Search results',
  key: activitySearchResultsKey.value,
  isLoading: false,
  collapsedByDefault: false,
  includeCompleted: true,
  badgeSeverity: 'success',
  isVisible: false,
});

const activitySections = ref<ActivityFilterSection[]>([{ ...recentlyCreated.value }, { ...searchResults.value }]);

const savedSections = computed(() => activitySections.value.filter(section => section.key !== recentlyCreatedActivitiesKey.value && section.key !== activitySearchResultsKey.value));

const activitySectionLookup = ref<Map<number, Set<string>>>(new Map());

const activityFilter = ref<ActivityFilter>({ ...deepClone(emptyActivityFilter), includeIncomplete: true });

const getSectionIndex = (key: string) => activitySections.value.findIndex(section => section.key === key);

const onActivityFilterApplied = () => {
  const idx = getSectionIndex(activitySearchResultsKey.value);
  const results = activitySections.value[idx];

  results.activities = [];
  results.isLoading = true;
  results.isVisible = true;

  if (isMobileFilterOpen.value) {
    isMobileFilterOpen.value = false;
  }

  fetchActivities(activitySearchResultsKey.value, activityFilter.value);
};

const onActivityFilterReset = () => {
  const idx = getSectionIndex(activitySearchResultsKey.value);
  const results = activitySections.value[idx];

  results.isLoading = false;
  results.isVisible = false;
  results.activities = [];

  if (isMobileFilterOpen.value) {
    isMobileFilterOpen.value = false;
  }

  activityFilter.value = deepClone(emptyActivityFilter);
};

const onActivityFilterSave = () => {
  activityFilter.value = createObjectFromSchema(savableActivityFilter, { ...activityFilter.value, badgeSeverity: 'primary' });
  isSaveFilterDialogOpen.value = true;
  if (isMobileFilterOpen.value) {
    isMobileFilterOpen.value = false;
  }
};

const saveActivityFilter = async () => {
  const lowestSortOrder = arrayHasValues(savedFilters.value)
    ? savedFilters.value
      .toSorted((a, b) => a.sortOrder - b.sortOrder)[0].sortOrder
    : 0;

  // Existing saved filters need to be sent with the request, because the BE uses the absence of filters to determine what to delete
  // eg if the user originally had [{id: 1}, {id: 2}, {id: 3}] sending the request [{id: 1}, {id: 2}] would mean "delete {id: 3}"
  // so since we're just saving a single filter here, we need to make sure to include existing ones so that they aren't deleted
  await saveActivityFilters([...savedFilters.value, { ...activityFilter.value, sortOrder: lowestSortOrder - 1 }]);
  onActivityFilterReset();
  isSaveFilterDialogOpen.value = false;
};

const hasActivityFilterChanged = computed(() => JSON.stringify(activityFilter.value) !== JSON.stringify(emptyActivityFilter));

const itemCount = computed(() => activitySections.value?.flatMap(s => s.activities)?.length ?? 0);

const updateActivityIdMap = (id: number, sectionKey: string) => {
  if (!activitySectionLookup.value.has(id)) {
    activitySectionLookup.value.set(id, new Set([sectionKey]));
  } else {
    const set = activitySectionLookup.value.get(id);
    set.add(sectionKey);
  }
};

const onActivityCreated = (activity: Activity) => {
  const idx = getSectionIndex(recentlyCreatedActivitiesKey.value);
  const section = activitySections.value[idx];

  section.isVisible = true;
  section.activities.push(activity);
  updateActivityIdMap(activity.id, recentlyCreatedActivitiesKey.value);
};

const onActivityUpdated = (activity: Activity) => {
  const sectionKeys = activitySectionLookup.value.get(activity.id);
  sectionKeys?.forEach(key => {
    const idx = getSectionIndex(key);
    const section = activitySections.value[idx];
    const activityIndex = activitySections.value[idx].activities?.findIndex(a => a.id === activity.id);
    if (activityIndex === -1) {
      return;
    }

    if (activity.completedAt && !section.includeCompleted) {
      section.activities.splice(activityIndex, 1);
    } else {
      section.activities[activityIndex] = activity;
    }
  });
};

const onActivityDeleted = (id: number) => {
  const sectionKeys = activitySectionLookup.value.get(id);
  sectionKeys?.forEach(key => {
    const idx = getSectionIndex(key);
    const section = activitySections.value[idx];
    const activityIdx = section.activities?.findIndex(a => a.id === id);
    if (activityIdx !== -1) {
      section.activities.splice(activityIdx, 1);
    }
  });
};

provide<ActivityAddedEvent>(InjectEventNames.crm.activities.activityCreated, onActivityCreated);
provide<ActivityUpdatedEvent>(InjectEventNames.crm.activities.activityUpdated, onActivityUpdated);
provide<ActivityDeletedEvent>(InjectEventNames.crm.activities.activityDeleted, onActivityDeleted);

const onLoadMoreActivities = async (sectionKey: string) => {
  const useTransientFilter = sectionKey === activitySearchResultsKey.value;

  if (useTransientFilter) {
    fetchActivities(sectionKey, activityFilter.value);
  } else {
    fetchActivities(sectionKey);
  }
};

const getNextUntitledSectionName = (filters: SavedActivityFilter[]) => {
  const untitled = filters.filter(section => section.filterName?.startsWith('Untitled Section'));
  if (!arrayHasValues(untitled)) {
    return 'Untitled Section';
  }

  const highest = untitled.map(f => getNumberAtEndOfString(f.filterName) ?? 0)
    .sort((a, b) => a - b)
    .pop();

  return `Untitled Section ${highest + 1}`;
};

const saveActivityFilters = async (filters: SavedActivityFilter[]) => {
  isPageSettingsOpen.value = false;

  filters.forEach(filter => {
    if (isEmptyString(filter.filterName)) {
      filter.filterName = getNextUntitledSectionName(combineArrays(activitySections.value, filters));
    }
  });

  const result = await saveFilters(filters);
  if (result) {
    savedFilters.value = result;
    activitySections.value = [{ ...recentlyCreated.value }, { ...searchResults.value }];
    result.sort((a, b) => a.sortOrder - b.sortOrder).forEach(filter => createSectionForFilter(filter));

    showSuccess('Your settings have been successfully saved');
  }
};

const fetchActivities = async (key: string, filter?: ActivityFilter) => {
  const idx = activitySections.value.findIndex(section => section.key === key);
  if (idx === -1) {
    return;
  }

  activitySections.value[idx].isLoading = true;

  const appliedFilter = filter !== undefined ? filter : transformVariableFilterDaysToFixedDates(activitySections.value[idx]);

  if (activitySections.value[idx].nextCursor) {
    appliedFilter.nextCursor = activitySections.value[idx].nextCursor;
  }

  const hadExistingActivities = arrayHasValues(activitySections.value[idx].activities);
  let lastActivityId: number;

  const response = await getFilteredActivities(appliedFilter);
  activitySections.value[idx].isLoading = false;

  if (arrayHasValues(response.activities)) {
    activitySections.value[idx].activities.push(...response.activities);

    if (hadExistingActivities) {
      lastActivityId = response.activities[0].id;
    }

    if (response.meta?.nextCursor) {
      activitySections.value[idx].nextCursor = response.meta.nextCursor;
    } else {
      activitySections.value[idx].nextCursor = undefined;
    }

    response.activities.forEach(activity => updateActivityIdMap(activity.id, key));
    processActivityExclusions();

    // This is in a timeout to allow the fade-in animation to complete before attempting to scroll
    setTimeout(() => {
      if (lastActivityId) {
        const activityItemEl = document.querySelector(`div[data-activity-id="${lastActivityId}"]`);
        if (activityItemEl) {
          activityItemEl.scrollIntoView({ block: 'start', behavior: 'smooth', inline: 'nearest' });
        }
      }
    }, 1000);
  }
};

const createSectionForFilter = (savedFilter: SavedActivityFilter) => {
  const section: ActivityFilterSection = {
    ...savedFilter,
    nextCursor: undefined,
    activities: [],
    isLoading: false,
    isVisible: true,
  };

  const idx = getSectionIndex(section.key);

  if (idx === -1) {
    activitySections.value.push(section);
  } else {
    activitySections.value[idx] = section;
  }

  if (!section.immediateLoad) {
    return;
  }

  fetchActivities(section.key);
};

const processActivityExclusions = () => activitySections.value.filter(section => arrayHasValues(section.excludeFromOtherSections))
  .forEach(section => section.excludeFromOtherSections.forEach(otherSectionKey => {
    const thisSectionActivities = new Set(section.activities.map(activity => activity.id));
    const otherSectionIdx = getSectionIndex(otherSectionKey);
    activitySections.value[otherSectionIdx].activities = activitySections.value[otherSectionIdx].activities.filter(activity => !thisSectionActivities.has(activity.id));
  }));

const reloadPage = () => {
  onActivityFilterReset();
  onPageLoad();
};

const onPageLoad = async () => {
  const savedActivityFilters = await getSavedFilters();
  savedFilters.value = savedActivityFilters;
  isContentLoading.value = false;

  savedActivityFilters
    ?.sort((a, b) => a.sortOrder - b.sortOrder)
    ?.forEach(filter => createSectionForFilter(filter));
};

const onResetFiltersToDefault = async () => {
  const success = await resetActivityFiltersToDefault();
  if (success) {
    isPageSettingsOpen.value = false;

    for (let i=0; i<activitySections.value.length; i++) {
      const { key } = activitySections.value[i];
      if (key !== recentlyCreatedActivitiesKey.value && key !== activitySearchResultsKey.value) {
        activitySections.value.splice(i, 1);
      }
    }

    onPageLoad();
  }
};

onMounted(() => {
  onPageLoad();

  // This is fired when the page is refreshed via the speed dial or sidebar
  window.addEventListener(CustomEventNames.crm.activities.reloadActivityHome, reloadPage);
});

onBeforeUnmount(() => window.removeEventListener(CustomEventNames.crm.activities.reloadActivityHome, reloadPage));
</script>
<template>
  <PageLayout :content-loading="isContentLoading">
    <template #headerTopRow>
      <SplitBar>
        <template #left>
          <Toolbar align="left">
            <Avatar
              size="normal"
              shape="circle"
              :icon="ObjectIconography.Activity.icon"
              :style="{backgroundColor: ObjectIconography.Activity.backgroundColor, color: ObjectIconography.Activity.color}"
            />
            <Typography variant="page-title">
              {{ ObjectIconography.Activity.plural }}
            </Typography>
            <Badge size="large" severity="secondary" :value="itemCount" />
          </Toolbar>
        </template>
        <template #right>
          <Toolbar>
            <Button
              icon="pi pi-cog"
              aria-label="Page settings"
              variant="text"
              :dt="Noir.button"
              rounded
              @click="isPageSettingsOpen = true"
            />
          </Toolbar>
        </template>
      </SplitBar>
    </template>
    <template #headerBottomRow>
      <SplitBar>
        <template #left>
          <div class="flex 2xl:hidden">
            <Button
              icon="pi pi-filter"
              variant="text"
              aria-label="Open filter options"
              rounded
              @click="isMobileFilterOpen = true"
            />
          </div>
          <div class="hidden 2xl:flex">
            <ActivityFilterEditor
              v-model="activityFilter"
              mode="filter"
              layout="row"
              :type-selections="selections.activities.types"
              :method-selections="selections.activities.methods"
              :purpose-selections="selections.activities.purposes"
              :user-selections="selections.salesScopedUsers"
              :save-disabled="!hasActivityFilterChanged"
              :search-disabled="!hasActivityFilterChanged"
              :reset-disabled="!hasActivityFilterChanged"
              @search="onActivityFilterApplied"
              @reset="onActivityFilterReset"
              @save="onActivityFilterSave"
            />
          </div>
        </template>
        <template #right>
          <AddActivityButton />
        </template>
      </SplitBar>
    </template>
    <ActivitiesHome
      :sections="activitySections"
      @section-load-more="onLoadMoreActivities"
      @activity-updated="onActivityUpdated"
      @activity-deleted="onActivityDeleted"
    />
  </PageLayout>
  <Dialog
    v-model:visible="isPageSettingsOpen"
    header="Page settings"
    position="top"
    class="w-[90vw] lg:w-[35rem] max-h-[90vh] lg:max-h-[66vh]"
    modal
  >
    <ActivitiesPageSettings
      :filters="savedSections"
      @save="saveActivityFilters"
      @cancel="isPageSettingsOpen = false"
      @reset-filters="onResetFiltersToDefault"
    />
  </Dialog>
  <Dialog
    v-model:visible="isSaveFilterDialogOpen"
    header="Save filter"
    position="top"
    class="w-[90vw] lg:w-[35rem] max-h-[90vh] lg:max-h-[66vh]"
    modal
  >
    <Stack>
      <ActivityFilterEditor
        v-model="activityFilter"
        mode="editor"
        layout="column"
        :type-selections="selections.activities.types"
        :method-selections="selections.activities.methods"
        :purpose-selections="selections.activities.purposes"
        :user-selections="selections.salesScopedUsers"
      />
      <Toolbar>
        <Button
          label="Cancel"
          severity="secondary"
          size="small"
          @click="isSaveFilterDialogOpen = false"
        />
        <Button label="Save" size="small" @click="saveActivityFilter" />
      </Toolbar>
    </Stack>
  </Dialog>
  <Dialog
    v-model:visible="isMobileFilterOpen"
    header="Filter Activities"
    position="top"
    class="w-[90vw] max-h-[90vh] lg:w-[66vw]"
    modal
  >
    <ActivityFilterEditor
      v-model="activityFilter"
      mode="filter"
      layout="column"
      :type-selections="selections.activities.types"
      :method-selections="selections.activities.methods"
      :purpose-selections="selections.activities.purposes"
      :user-selections="selections.salesScopedUsers"
      :save-disabled="!hasActivityFilterChanged"
      :search-disabled="!hasActivityFilterChanged"
      :reset-disabled="!hasActivityFilterChanged"
      @search="onActivityFilterApplied"
      @reset="onActivityFilterReset"
      @save="onActivityFilterSave"
    />
  </Dialog>
</template>
